// src/pages/Profile.jsx
import { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import {
  Building2,
  MapPin,
  Landmark,
  Edit2,
  Check,
  Phone,
  Mail,
  Users,
  Layers,
} from "lucide-react";
import { ProfileSkeleton } from "../components/Skeleton";
import "./Profile.css";

const Profile = () => {
  const { buyer, updateBuyer } = useBuyer();
  const buyerId = buyer?.buyerId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Profile Form & Firestore Data State
  const [profileData, setProfileData] = useState({
    organizationName: "",
    gstrStatus: "",
    gstinNumber: "",
    businessType: "",
    teamSize: "",
    organizationContact: "",
    organizationEmail: "",
    transactionalMobile: "",
    transactionalEmail: "",
    shopNumber: "",
    buildingName: "",
    streetAddress: "",
    contactCity: "",
    contactDistrict: "",
    contactState: "",
    contactPinCode: "",
    bankName: "",
    ifscCode: "",
    accountType: "",
    accountHolderName: "",
    accountNumber: "",
    storeLogoUrl: "",
    dealingPersons: [],
    segments: {},
  });

  // Fetch full buyer profile from Firestore
  const fetchProfile = useCallback(async () => {
    if (!buyerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const docRef = doc(db, "buyerregistrations", buyerId);
      const snap = await getDoc(docRef);

      const segmentRef = doc(db, "BuyerSegmentRegistrations", buyerId);
      const segmentSnap = await getDoc(segmentRef);

      const segmentsData = segmentSnap.exists()
        ? segmentSnap.data().segments || {}
        : buyer?.operatorsBySegment || {};

      if (snap.exists()) {
        const d = snap.data();
        setProfileData({
          organizationName: d.organizationName || "",
          gstrStatus: d.gstrStatus || "registered",
          gstinNumber: d.gstinNumber || "",
          businessType: d.businessType || "",
          teamSize: d.teamSize || "",
          organizationContact: d.organizationContact || buyer?.mobile || "",
          organizationEmail: d.organizationEmail || "",
          transactionalMobile: d.transactionalMobile || d.organizationContact || "",
          transactionalEmail: d.transactionalEmail || d.organizationEmail || "",
          shopNumber: d.shopNumber || "",
          buildingName: d.buildingName || "",
          streetAddress: d.streetAddress || "",
          contactCity: d.contactCity || "",
          contactDistrict: d.contactDistrict || "",
          contactState: d.contactState || "",
          contactPinCode: d.contactPinCode || "",
          bankName: d.bankName || "",
          ifscCode: d.ifscCode || "",
          accountType: d.accountType || "",
          accountHolderName: d.accountHolderName || "",
          accountNumber: d.accountNumber || "",
          storeLogoUrl: d.storeLogoUrl || "",
          dealingPersons: d.dealingPersons || [],
          segments: segmentsData,
        });
      } else {
        // Fallback default from BuyerContext
        setProfileData((prev) => ({
          ...prev,
          organizationContact: buyer?.mobile || "",
          segments: segmentsData,
        }));
      }
    } catch (err) {
      console.error("[Profile] Error loading buyer profile:", err);
    } finally {
      setLoading(false);
    }
  }, [buyerId, buyer]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!buyerId) return;

    try {
      setSaving(true);
      const docRef = doc(db, "buyerregistrations", buyerId);
      const updatePayload = {
        ...profileData,
        updatedAt: serverTimestamp(),
      };
      await setDoc(docRef, updatePayload, { merge: true });

      updateBuyer({
        mobile: profileData.organizationContact || buyer?.mobile,
      });

      alert("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      console.error("[Profile] Error saving profile:", err);
      alert(`Failed to update profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header title="My Profile" />
        <ProfileSkeleton />
      </>
    );
  }

  const initialLetter = (profileData.organizationName || "B")[0].toUpperCase();

  return (
    <>
      <Header title="My Profile" />
      <div className="profile-page">
        {/* Hero Card */}
        <div className="profile-hero-card">
          <div className="profile-hero-left">
            <div className="profile-avatar-circle">
              {profileData.storeLogoUrl ? (
                <img src={profileData.storeLogoUrl} alt="Store Logo" className="profile-avatar-img" />
              ) : (
                <span>{initialLetter}</span>
              )}
            </div>
            <div className="profile-hero-info">
              <h1 className="profile-org-name">
                {profileData.organizationName || "Registered Buyer"}
              </h1>
              <div className="profile-hero-sub">
                <span className="profile-buyer-id-badge">{buyerId || "BUYER"}</span>
                {profileData.organizationContact && (
                  <span>
                    <Phone size={13} style={{ display: "inline", marginRight: "4px" }} />
                    {profileData.organizationContact}
                  </span>
                )}
                {profileData.organizationEmail && (
                  <span>
                    <Mail size={13} style={{ display: "inline", marginRight: "4px" }} />
                    {profileData.organizationEmail}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            {!isEditing ? (
              <button className="profile-edit-btn" onClick={() => setIsEditing(true)}>
                <Edit2 size={16} /> Edit Profile
              </button>
            ) : (
              <button
                className="profile-edit-btn profile-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                <Check size={16} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </div>

        {/* Profile Details Grid */}
        <div className="profile-grid">
          {/* Organization / Business Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <Building2 className="profile-card-icon" size={20} />
              <span>Business & Tax Information</span>
            </div>
            <div className="profile-field-list">
              <div className="profile-field-item">
                <span className="profile-field-label">Organization Name</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="organizationName"
                    value={profileData.organizationName}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.organizationName || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">GSTR Status</span>
                <span className="profile-field-value" style={{ textTransform: "capitalize" }}>
                  {profileData.gstrStatus || "Registered"}
                </span>
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">GSTIN / PAN Number</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="gstinNumber"
                    value={profileData.gstinNumber}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.gstinNumber || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">Business Type</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="businessType"
                    value={profileData.businessType}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.businessType || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">Team Size</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="teamSize"
                    value={profileData.teamSize}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.teamSize || "N/A"}</span>
                )}
              </div>
            </div>
          </div>

          {/* Address & Location Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <MapPin className="profile-card-icon" size={20} />
              <span>Registered Business Address</span>
            </div>
            <div className="profile-field-list">
              <div className="profile-field-item">
                <span className="profile-field-label">Shop / Building</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="shopNumber"
                    placeholder="Shop No"
                    value={profileData.shopNumber}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">
                    {[profileData.shopNumber, profileData.buildingName].filter(Boolean).join(", ") || "N/A"}
                  </span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">Street Address</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="streetAddress"
                    value={profileData.streetAddress}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.streetAddress || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">City & District</span>
                {isEditing ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="profile-field-input"
                      name="contactCity"
                      placeholder="City"
                      value={profileData.contactCity}
                      onChange={handleChange}
                    />
                    <input
                      className="profile-field-input"
                      name="contactDistrict"
                      placeholder="District"
                      value={profileData.contactDistrict}
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <span className="profile-field-value">
                    {[profileData.contactCity, profileData.contactDistrict].filter(Boolean).join(", ") || "N/A"}
                  </span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">State & Pincode</span>
                {isEditing ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="profile-field-input"
                      name="contactState"
                      placeholder="State"
                      value={profileData.contactState}
                      onChange={handleChange}
                    />
                    <input
                      className="profile-field-input"
                      name="contactPinCode"
                      placeholder="Pincode"
                      value={profileData.contactPinCode}
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <span className="profile-field-value">
                    {[profileData.contactState, profileData.contactPinCode].filter(Boolean).join(" - ") || "N/A"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Banking Information Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <Landmark className="profile-card-icon" size={20} />
              <span>Bank Account Details</span>
            </div>
            <div className="profile-field-list">
              <div className="profile-field-item">
                <span className="profile-field-label">Bank Name</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="bankName"
                    value={profileData.bankName}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.bankName || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">Account Holder Name</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="accountHolderName"
                    value={profileData.accountHolderName}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value">{profileData.accountHolderName || "N/A"}</span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">Account Number & Type</span>
                {isEditing ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="profile-field-input"
                      name="accountNumber"
                      placeholder="Account No"
                      value={profileData.accountNumber}
                      onChange={handleChange}
                    />
                    <input
                      className="profile-field-input"
                      name="accountType"
                      placeholder="Type"
                      value={profileData.accountType}
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <span className="profile-field-value">
                    {profileData.accountNumber ? `${profileData.accountNumber} (${profileData.accountType || "Current"})` : "N/A"}
                  </span>
                )}
              </div>

              <div className="profile-field-item">
                <span className="profile-field-label">IFSC Code</span>
                {isEditing ? (
                  <input
                    className="profile-field-input"
                    name="ifscCode"
                    value={profileData.ifscCode}
                    onChange={handleChange}
                  />
                ) : (
                  <span className="profile-field-value" style={{ fontFamily: "monospace" }}>
                    {profileData.ifscCode || "N/A"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Registered Segments Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <Layers className="profile-card-icon" size={20} />
              <span>Registered Segments & Operators</span>
            </div>
            {Object.keys(profileData.segments || {}).length === 0 ? (
              <div className="profile-field-value" style={{ color: "#64748b" }}>
                No segment registrations found.
              </div>
            ) : (
              <div className="profile-segments-container">
                {Object.entries(profileData.segments).map(([segment, opId]) => (
                  <div key={segment} className="profile-segment-pill">
                    <span className="profile-segment-title">{segment} Segment</span>
                    <span className="profile-segment-operator">Operator: {opId}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Dealing Persons Card */}
        {profileData.dealingPersons && profileData.dealingPersons.length > 0 && (
          <div className="profile-card" style={{ marginBottom: "24px" }}>
            <div className="profile-card-header">
              <Users className="profile-card-icon" size={20} />
              <span>Dealing Persons ({profileData.dealingPersons.length})</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" }}>
              {profileData.dealingPersons.map((person, idx) => (
                <div key={idx} className="profile-person-card">
                  <div className="profile-person-avatar">
                    {person.photoUrl ? (
                      <img src={person.photoUrl} alt={person.fullName} style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                    ) : (
                      (person.fullName || "P")[0].toUpperCase()
                    )}
                  </div>
                  <div className="profile-person-info">
                    <div className="profile-person-name">{person.fullName || "Unnamed Person"}</div>
                    <div className="profile-person-sub">
                      {[person.role, person.department].filter(Boolean).join(" • ") || "Dealing Officer"}
                    </div>
                    {person.contactNumber && (
                      <div className="profile-person-sub" style={{ marginTop: "4px" }}>
                        <Phone size={11} style={{ display: "inline", marginRight: "3px" }} />
                        {person.contactNumber}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;
